import { Component, type ErrorInfo, type ReactNode } from 'react';
import { captureUiError } from '../config/sentry';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

const STALE_MODULE =
  /Importing a module script failed|Failed to fetch dynamically imported module|error loading dynamically imported module/i;

const SW_RESET_KEY = 'lh-sw-reset-v2';

function isStaleModuleError(error: Error | null): boolean {
  return STALE_MODULE.test(error?.message ?? '');
}

async function clearStuckServiceWorker(): Promise<void> {
  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));
  }
  if ('caches' in window) {
    const keys = await caches.keys();
    await Promise.all(keys.map((key) => caches.delete(key)));
  }
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    captureUiError(error, info.componentStack ?? undefined);
    if (!isStaleModuleError(error) || sessionStorage.getItem(SW_RESET_KEY)) return;
    sessionStorage.setItem(SW_RESET_KEY, '1');
    void clearStuckServiceWorker().finally(() => {
      window.location.reload();
    });
  }

  private handleRetry = (): void => {
    if (!isStaleModuleError(this.state.error)) {
      this.setState({ hasError: false, error: null });
      return;
    }
    sessionStorage.removeItem(SW_RESET_KEY);
    void clearStuckServiceWorker().finally(() => {
      window.location.reload();
    });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div className="flex min-h-[50vh] flex-col items-center justify-center px-4 text-center">
          <h1 className="text-2xl font-bold text-gray-900">Something went wrong</h1>
          <p className="mt-2 max-w-md text-sm text-gray-600">
            {this.state.error?.message || 'An unexpected error occurred. Please try again.'}
          </p>
          <button type="button" onClick={this.handleRetry} className="btn-primary mt-6">
            Try Again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
